import React, { useState } from 'react';
import { trackColumnConfigEvent } from '../services/rumService';
import { ColumnConfig } from '../types/columns';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

interface ColumnConfigModalProps {
  columns: ColumnConfig[];
  onUpdateColumns: (columns: ColumnConfig[]) => void;
  onResetColumns: () => ColumnConfig[];
  trigger?: React.ReactNode;
}

export const ColumnConfigModal: React.FC<ColumnConfigModalProps> = ({
  columns,
  onUpdateColumns,
  onResetColumns,
  trigger,
}) => {
  const [localColumns, setLocalColumns] = useState<ColumnConfig[]>(columns);
  const [isOpen, setIsOpen] = useState(false);

  // Update local state when columns prop changes or when modal opens
  React.useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  // Reset local state when modal opens to ensure sync with props
  React.useEffect(() => {
    if (isOpen) {
      setLocalColumns(columns);

      // Track modal opening
      const visibleColumns = columns
        .filter(col => col.visible)
        .map(col => col.id);
      trackColumnConfigEvent('open_modal', visibleColumns, columns.length, {
        totalVisibleColumns: visibleColumns.length,
        requiredColumns: columns.filter(col => col.required).map(col => col.id),
      });
    }
  }, [isOpen, columns]);

  const handleColumnToggle = (columnId: string, checked: boolean) => {
    setLocalColumns(prev =>
      prev.map(col =>
        col.id === columnId ? { ...col, visible: checked } : col
      )
    );
  };

  const handleSave = () => {
    const visibleColumns = localColumns
      .filter(col => col.visible)
      .map(col => col.id);

    // Track the save action
    trackColumnConfigEvent(
      'save_changes',
      visibleColumns,
      localColumns.length,
      {
        changedColumns: localColumns
          .filter((col, index) => col.visible !== columns[index]?.visible)
          .map(col => col.id),
        totalVisibleColumns: visibleColumns.length,
      }
    );

    onUpdateColumns(localColumns);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setLocalColumns(columns);
    setIsOpen(false);
  };

  const handleReset = () => {
    // Reset columns and get the default state
    const resetColumns = onResetColumns();

    // Track the reset action
    trackColumnConfigEvent(
      'reset_default',
      resetColumns.filter(col => col.visible).map(col => col.id),
      resetColumns.length,
      {
        previousVisibleColumns: localColumns
          .filter(col => col.visible)
          .map(col => col.id),
      }
    );

    // Update local state immediately to reflect the reset
    setLocalColumns(resetColumns);
    setIsOpen(false);
  };

  const handleSelectAll = (checked: boolean) => {
    const visibleColumns = localColumns
      .filter(col => col.visible)
      .map(col => col.id);

    // Track the select all/deselect all action
    trackColumnConfigEvent(
      checked ? 'select_all' : 'deselect_all',
      visibleColumns,
      localColumns.length,
      {
        affectedColumns: localColumns
          .filter(col => !col.required)
          .map(col => col.id),
      }
    );

    setLocalColumns(prev =>
      prev.map(col => (col.required ? col : { ...col, visible: checked }))
    );
  };

  const nonRequiredColumns = localColumns.filter(col => !col.required);
  const visibleNonRequiredCount = nonRequiredColumns.filter(
    col => col.visible
  ).length;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="mr-2"
            >
              <path
                d="M3 5h10M3 8h10M3 11h10M1 2h14M1 14h14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Configure Columns
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl bg-white border border-gray-200 shadow-xl">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-lg font-semibold text-gray-900">
            Configure Table Columns
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/*
        <div className="text-xs text-gray-700 p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="font-medium">Selection Summary:</div>
            <div className="mt-1">{visibleCount} of {localColumns.length} columns selected</div>
            {requiredColumns.length > 0 && (
              <div className="mt-1 text-green-700">
                {requiredColumns.length} required column{requiredColumns.length > 1 ? 's' : ''} always visible
              </div>
            )}
        </div> */}

          <div className="max-h-80 overflow-y-auto space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
            {localColumns.map(column => (
              <div
                key={column.id}
                className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
              >
                <Checkbox
                  id={column.id}
                  checked={column.visible}
                  disabled={column.required}
                  onCheckedChange={checked =>
                    handleColumnToggle(column.id, checked as boolean)
                  }
                />
                <label
                  htmlFor={column.id}
                  className={`flex-1 text-sm cursor-pointer font-medium ${
                    column.required ? 'text-gray-500' : 'text-gray-900'
                  }`}
                >
                  {column.label || 'Actions'}
                  {column.required && (
                    <span className="ml-2 text-xs font-normal text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                      (Required)
                    </span>
                  )}
                </label>
              </div>
            ))}
          </div>

          {/* Check All / Uncheck All Buttons */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-700">
              {visibleNonRequiredCount} of {nonRequiredColumns.length} optional
              columns selected
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSelectAll(true)}
                className="text-green-700 border-green-300 hover:bg-green-50"
              >
                Check All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSelectAll(false)}
                className="text-red-700 border-red-300 hover:bg-red-50"
              >
                Uncheck All
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-between pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleReset}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-50"
          >
            Reset to Default
          </Button>

          <div className="flex space-x-2">
            <DialogClose asChild>
              <Button
                variant="outline"
                onClick={handleCancel}
                className="hover:bg-gray-50"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
